import type { FieldError } from '@atek/estimate-pricing';
import { ApiError } from '@/lib/api';
import type { DeckWarning } from '../types';

/** Body of a V4 error: `{ error: CODE, message, fields?, problems?, ... }`. */
export interface ApiErrorBody {
  error?: string;
  message?: string;
  fields?: FieldError[];
  problems?: DeckWarning[];
  updatedByName?: string | null;
  quoteId?: string;
  [key: string]: unknown;
}

export function errorBody(err: unknown): ApiErrorBody {
  if (err instanceof ApiError && err.data && typeof err.data === 'object') return err.data as ApiErrorBody;
  return {};
}

/**
 * The server's error code; CLIENT_TIMEOUT when the browser stopped waiting; GATEWAY_TIMEOUT when a proxy
 * answered 504 without a JSON body (its own page), so the outcome doesn't depend on the proxy's timeout.
 */
export function errorCode(err: unknown): string | null {
  const { error } = errorBody(err);
  if (typeof error === 'string' && error) return error;
  if (err instanceof ApiError && err.isTimeout) return 'CLIENT_TIMEOUT';
  if (err instanceof ApiError && err.status === 504) return 'GATEWAY_TIMEOUT';
  return null;
}

/**
 * The sentence to show. src/lib/api.ts replaces 403/502/503/504 messages with generic text, so the server's
 * own message (which names the folder, the template or the fix) wins when it sent one.
 */
export function errorMessage(err: unknown): string {
  const { message } = errorBody(err);
  if (typeof message === 'string' && message) return message;
  if (err instanceof Error && err.message) return err.message;
  return String(err);
}

/** Field errors keyed by path (`inputs.equipment[0].qty`, `title`, `presenter.email`, ...). */
export function fieldErrorMap(err: unknown): Record<string, string> {
  return Object.fromEntries((errorBody(err).fields ?? []).map((f) => [f.path, f.message]));
}

/** After these the operation may still complete on the server: reload instead of retrying. */
export const MAY_STILL_FINISH = new Set(['CLIENT_TIMEOUT', 'GATEWAY_TIMEOUT', 'UPSTREAM_TIMEOUT', 'GOOGLE_TIMEOUT']);
